// Simplifying the Science. Exclusive coaches webinar masterclass.
// Mux: oH4cTcGDxDetPYYxQESs3Z7KLuWgoIP00ET8iidOsAE8 (1:55:38)
//
// The slides are BAM's editorial summary of the webinar, not a
// transcript. Only text inside quotation marks is Coleman's own words,
// and every quote carries the time he says it. talkQuotes.test.js checks
// each one against knowledge-base/transcripts/masterclass/
// simplifying-the-science.md (the webinar's captions), so a quote that
// isn't in the talk can't ship. The summary keeps his hedges and says
// when he's citing research or giving his opinion, and it stays in the
// third person so it can't read as his voice.
// No em dashes anywhere in user-visible copy.

export const SIMPLIFYING_SCIENCE_MODULE_KEY = "simplifying-the-science";

const VIDEO = "oH4cTcGDxDetPYYxQESs3Z7KLuWgoIP00ET8iidOsAE8";

export const SIMPLIFYING_SCIENCE_MODULE = {
  key: SIMPLIFYING_SCIENCE_MODULE_KEY,
  title: "Simplifying the Science",
  sub: "An exclusive coaches webinar on what the research actually says, and how to use it without overcomplicating practice.",
  coverVideoId: VIDEO,
  duration: "1h 56min webinar plus 13 slides",
  chapters: [
    {
      id: "watch",
      title: "Watch the webinar",
      blocks: [
        { kind: "text", md:
          "1 hour 56 minutes. Watch it straight through if you have the time. The slides below follow the spine of the talk, in order." },
        { kind: "text", md:
          "**About these slides.** They're BAM's summary of the webinar, written from the recording. They're not a transcript. Only the lines in quotation marks are Coleman's own words, each with the time he says it in the video." },
        { kind: "video", muxPlaybackId: VIDEO,
          caption: "Simplifying the Science. Full webinar." },
      ],
    },

    {
      id: "lens-not-law",
      title: "1. Science is a lens. Not a law.",
      blocks: [
        { kind: "text", md:
          "\"It's not a plug and play formula. ... It's definitely not a final answer.\" (5:16)" },
        { kind: "text", md:
          "Science is very valuable, because it shows how humans learn and develop. Take it with a grain of salt anyway. A lot of the studies have small sample sizes and populations that don't look like the players you coach, though the past half decade has brought more research on elite and solid high school athletes. Most are short interventions, weeks or months, when player development runs on years. They're controlled versions of a chaotic game, and when a study does look at the real game, \"it's impossible for us to know exactly what the source of these improvements are.\" (4:28)" },
        { kind: "text", md:
          "**What science is good for:**\n\n- A lens to see the game more clearly\n- A framework to build on\n- A tool to ask questions, stay curious and spark ideas\n- A way to improve your coaching decisions" },
        { kind: "text", md:
          "**What it is not:**\n\n- The only guiding principle to follow\n- Useful without coaching skill\n- Perfectly transferable from a study to your gym" },
        { kind: "text", md:
          "And before any of this matters, watch out for survivorship bias. Many times the best players got there despite a method, not because of it. Or they picked up a method after they were already at the top. Just because your mentor did it doesn't make it law. Clear your canvas." },
      ],
    },

    {
      id: "cla-one-sentence",
      title: "2. The Constraints-Led Approach in one sentence.",
      blocks: [
        { kind: "text", md:
          "\"It's the approach that players learn best by solving problems rather than being told what to do.\" (10:10)" },
        { kind: "text", md:
          "That's the whole definition. The CLA designs environments and tasks that pose problems for athletes to solve, and guides them toward better solutions. It's an approach you can bring into any method: on air, live, film, even psychological work." },
        { kind: "text", md:
          "**The misconceptions to drop:**\n\n- The CLA is not just small-sided games. It's just as effective on air\n- The CLA is not anti-reps\n- The CLA is not anti-technique\n- The CLA is not chaos, random drills, or constraints for fun" },
        { kind: "text", md:
          "On reps and technique, Coleman is careful with how strongly he puts it: \"I would argue that the CLA actually improves the quality of repetition.\" (9:16) And: \"I would say it actually is a better tool, or maybe not better, but a tool, a very powerful tool to actually build better technique.\" (9:44)" },
      ],
    },

    {
      id: "three-constraints",
      title: "3. The three constraints.",
      blocks: [
        { kind: "text", md:
          "Every problem you put a player in is shaped by three families of constraints." },
        { kind: "text", md:
          "**Environmental.** What's around them. The court, the lighting, the surface, the rim, the culture they grew up in. Mostly something you learn about and evaluate, but you can also adjust it (lower the rim, dim the lights, take them outside on a hot day)." },
        { kind: "text", md:
          "**Individual.** What they bring with them. Wingspan, eye dominance, ankle stiffness, confidence, prior sports, mental models. You can't easily change these in a session, but you have to understand them to design well." },
        { kind: "text", md:
          "**Task.** The rules of the drill itself. Spacing, timing, who starts where, what defenders can or can't do, what the offense has to do. This is the cockpit you actually fly the drill from." },
      ],
    },

    {
      id: "constraint-cockpit",
      title: "4. Tiny lever changes. Completely different drill.",
      blocks: [
        { kind: "text", md:
          "**Same drill, change one constraint, get a totally different problem and a totally different solution.**" },
        { kind: "text", md:
          "Example from the webinar: a finishing drill where players jump from outside the paint produces a lot of floaters. Change one rule, jump from outside and land inside, and you get more aggressive layups, contact mid-air, ground covered. Same setup. Different rule. Different game." },
        { kind: "text", md:
          "**Levers worth pulling on any drill:**\n\n- How the defender starts (hip-to-hip, late, with hands behind back, holding a ball)\n- The shape of the space (how wide, at what angle you place the poles)\n- A creativity rule (you can't use the same solution twice)\n- A scoring rule (you have to get a stop to earn offense)\n- A starting condition (already at a disadvantage)" },
        { kind: "text", md:
          "Once you see drills through this lens, every button you press creates a different problem, and therefore different solutions. You stop telling players what to do and start designing the problem you want them to solve. And that's where they can surprise you." },
      ],
    },

    {
      id: "why-constraints-work",
      title: "5. Why this actually works.",
      blocks: [
        { kind: "text", md:
          "Why not just tell athletes what to do and hope they improve? Three reasons." },
        { kind: "text", md:
          "**Self-organization.** Players find their own solutions. Some you would never have thought of, some that fit their individual constraint profile better than your preferred technique would. \"No matter how good you are, as a coach, you never know what will actually work best for an athlete.\" (19:08)" },
        { kind: "text", md:
          "**They learn how to learn.** Every constrained rep is a problem they have to solve. Every solved problem makes them a better problem solver, so they can learn more on their own." },
        { kind: "text", md:
          "**Adaptability.** When a new defender, a new situation, or new chaos shows up, they're able to adapt to what the environment, the defense and the situation give them. That's what they've been training the whole time." },
      ],
    },

    {
      id: "perception-action",
      title: "6. Movement isn't isolated. It's perception, decision, action.",
      blocks: [
        { kind: "text", md:
          "\"The core concept behind this is that movement is not isolated. We don't move just to move.\" (39:40)" },
        { kind: "text", md:
          "The cycle: perceive (where's the defender, where's help, how the player feels) → decide (which solution) → act (execute) → and the new position the player is in starts the cycle over again. The perception has to happen in under a second." },
        { kind: "text", md:
          "By Coleman's estimate, traditional player development trains the action outside of perception and decision 99% of the time. His own clip shows the cost: a reactive move is \"less technically sound than a planned movement\" (42:39) even with a simple audio cue, so you have to train for it." },
        { kind: "text", md:
          "**Practical implication:** \"I'm not saying that all movement should be trained or built in the context of defenders or a reaction, but more of it should be.\" (43:10) He also points to \"the science behind this, which has shown how much more effective it is to train movement ingrained in the environment.\" (43:35)" },
      ],
    },

    {
      id: "action-capacity",
      title: "7. Action capacity still matters.",
      blocks: [
        { kind: "text", md:
          "**Building the ceiling is real work.**" },
        { kind: "text", md:
          "Sometimes a player perceives the opportunity. They decide on the right move. And they still can't execute it because they don't have the control or the quick pop off the ground. That's an action capacity problem." },
        { kind: "text", md:
          "**On-air training is for:**\n\n- General control of the ball\n- Fluidity and control of the jumper\n- Physical qualities like ankle stiffness and body control on finishes\n- Building puzzle pieces of bigger skills" },
        { kind: "text", md:
          "Shooting is one of the best qualities to build on air: \"you can become a really good shooter or at least take steps towards becoming a really good shooter without the entire context of the game.\" (45:05)" },
        { kind: "text", md:
          "Don't try to make on-air work look game-like. With no defense, it won't be. Use it to expand the edges of what they can do, and keep in mind how much perception and decision matter. Too many players stop their training at this stage." },
      ],
    },

    {
      id: "functional-movement-variability",
      title: "8. The best players are not the most consistent.",
      blocks: [
        { kind: "text", md:
          "Functional movement variability is \"the ability to adjust your technique to fit the situation while still getting the same outcome.\" (47:34)" },
        { kind: "text", md:
          "In the clip, the same player hits a run of floaters, and every one is a little, if not a decent amount, different. Different jump foot, different angle, different release angle, rotating or not, different speed." },
        { kind: "text", md:
          "\"This is a better determinant of expertise than consistency.\" (48:06) Higher-level players are not the ones who repeat the same thing best. They are the ones who adjust their technique to the environment and the problem in front of them." },
        { kind: "text", md:
          "Two caveats:\n\n- Younger players still need to stabilize a baseline of technique first\n- A jumper has more attractors than a layup. Floaters have a bit more room to vary, and layups are fluctuator-heavy: overhand, underhand, different jump types, and still make it work" },
      ],
    },

    {
      id: "attractors-fluctuators",
      title: "9. Attractors and fluctuators. What to lock, what to let move.",
      blocks: [
        { kind: "text", md:
          "**Attractors are the parts of a technique you want to keep stable through chaos. Fluctuators are the parts you want players to learn to move.**" },
        { kind: "text", md:
          "**Jumper attractors (keep stable):**\n\n- Release window\n- Hand placement\n- Quiet eye on the rim\n- Energy transfer and rhythm" },
        { kind: "text", md:
          "**Jumper fluctuators (let them adapt):**\n\n- Release height and timing\n- Torso angle\n- Jump height\n- Where you drift" },
        { kind: "text", md:
          "In Coleman's opinion, this is where much shooting training goes wrong: \"people do a decent job of trying to keep these consistent, but they do not provide enough chaos.\" (51:27) The goal is to keep the attractors stable while the fluctuators move around them, inside tasks that actually challenge you." },
      ],
    },

    {
      id: "motor-noise",
      title: "10. Humans are not robots.",
      blocks: [
        { kind: "text", md:
          "\"It is because humans are not robots. We literally cannot repeat the same movement perfectly.\" (52:15)" },
        { kind: "text", md:
          "There's noise in how the nervous system fires the signal, in how muscle fibers fire, in how muscles time with each other, in how you perceive what's around you. Every rep is a little different by default. Coleman dates this to his own change in thinking: \"What I didn't even realize until a couple years ago is that it's not just about adjusting to the game.\" (52:03)" },
        { kind: "text", md:
          "\"What the research has shown is that skilled players actually aren't the most consistent after a certain point.\" (53:18) Beginners with the most consistent technique have the highest success rate, because they're still stabilizing it. Past that point, experts are the best at dealing with motor noise and adjusting their technique to it." },
        { kind: "text", md:
          "Noise gets amplified by:\n\n- Fatigue\n- Pressure and overthinking\n- Defenders, contact, environmental constraints\n\nOnce you accept that athletes can't move the same way twice, you train them to deal with it, instead of always trying to force technique into being perfect." },
      ],
    },

    {
      id: "practice-variability",
      title: "11. Practice variability. A new problem every rep.",
      blocks: [
        { kind: "text", md:
          "\"Simplest definition of this is just posing a new problem each time.\" (55:43)" },
        { kind: "text", md:
          "Coleman separates two kinds of variability. Functional movement variability is adjusting your technique and still succeeding. Practice variability is how you apply variability in practice." },
        { kind: "text", md:
          "Solving the same problem 10 times gets monotonous fast. Solving 10 slightly different problems builds contextual interference, which forces real thinking instead of recitation. Ask a kid what 8 times 8 is over and over and it gets monotonous. A different problem every time takes real attention." },
        { kind: "text", md:
          "**Lower to higher variability:**\n\n- Repetition without repetition. The same drill or skill, with slightly different constraints each rep.\n- Interleaving. Switch from one skill or concept to another between reps.\n\nDifferential learning (slide 13) is a separate tool: exploring the edges of technique." },
        { kind: "text", md:
          "Match variability to skill level. Too much for a beginner puts them in a thrash zone. Too little for an expert puts them in a comfort zone. The job is to keep finding the optimal challenge point." },
      ],
    },

    {
      id: "where-block-fits",
      title: "12. Where block training still fits.",
      blocks: [
        { kind: "text", md:
          "\"I'm not saying that everything has to be incredibly variable or variable at all.\" (1:01:34)" },
        { kind: "text", md:
          "Blocked work sharpens one tool in the toolbox, which isn't inherently bad. The drawback is that it touches one solution out of many, with no contextual interference, so players recite instead of solving." },
        { kind: "text", md:
          "**Where block training fits:**\n\n- Stabilizing movement patterns, especially with younger players whose technique is all over the place\n- A higher-level player who is a true beginner at one specific skill (try it five times in a row, then go back to the game)\n- Feel-good work that keeps the game fun" },
        { kind: "text", md:
          "On feel-good work, the goal isn't less of it: \"It's not less feel-good work. It's that we're getting more out of their feel-good work.\" (1:03:52)" },
        { kind: "text", md:
          "**If a player is in the thrash zone** (shooting 10 to 20% on every drill), Coleman's fix is to decrease the variability and lean toward a very simple version of repetition without repetition. More often, though, he sees athletes stuck in the comfort zone, where the fix is more contextual interference and challenge." },
      ],
    },

    {
      id: "differential-learning",
      title: "13. Differential learning. The fingerprint.",
      blocks: [
        { kind: "text", md:
          "**Think of shooting like scanning a fingerprint.**" },
        { kind: "text", md:
          "If you only press your thumb in one place, the scanner only sees one piece. Move it around and the scanner builds a full image. Same with a skill. The more positions you expose your brain to, the more complete and flexible the map of how to move becomes." },
        { kind: "text", md:
          "**Examples that look weird but build real qualities:**\n\n- Oversized or undersized basketball reps\n- Scoop shots starting from the ground\n- 360 jumpers (jump, spin, land, pop back into the shot)\n- Finishes that touch your shoulder or knee before the rim\n- Defenders holding a ball over their head while recovering" },
        { kind: "text", md:
          "You're not training those exact shots for game use. You're training the ability to adapt and solve problems, and the qualities underneath, like balance, rotational control and body control." },
        { kind: "text", md:
          "\"This isn't going to be the bulk of our training with most players, but it's an immensely valuable method.\" (1:11:08)" },
      ],
    },

    {
      id: "feel-vs-iq",
      title: "14. Feel cannot be taught. IQ can be supplemented.",
      blocks: [
        { kind: "text", md:
          "**Two kinds of basketball decisions. They get built differently.**" },
        { kind: "text", md:
          "**Feel.** Usually under 400 milliseconds, by Coleman's breakdown. The instinctive reads (the quick counter, the pass window you just see, the mid-air switch to the off hand). He describes different parts of the brain running these, and says they can't be processed consciously in the time available." },
        { kind: "text", md:
          "**IQ.** Half a second and up. The conscious reads (noticing a pattern in the help defense, deciding to shoot or not). There's time to think." },
        { kind: "text", md:
          "**How you build each:**\n\n- Feel is learned through reps without repetition in small-sided games, not taught. Stay away from cueing players to think through an instinct.\n- IQ can be supplemented with verbal coaching, and preferably visual coaching: demonstration and film." },
        { kind: "text", md:
          "\"Feel cannot be taught, but it can be learned.\" (1:22:55) And: \"I'm not saying that because I don't trust my or your ability to teach, but it's science. We cannot process these things in that brief amount of time.\" (1:23:57)" },
        { kind: "text", md:
          "Feel can still be supplemented: \"Showing or having athletes watch a ton of film to expose them to different movement solutions here is valuable.\" (1:25:01)" },
      ],
    },
  ],
};
